import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { useAuth } from 'lib/auth'
import { yupResolver } from '@hookform/resolvers'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import { draftToMarkdown } from 'markdown-draft-js'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import LinearProgress from '@material-ui/core/LinearProgress'
import { dollarsToCents } from 'lib/utils/number'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import { gtmEvent } from 'lib/gtm'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import CheckIcon from 'components/material-symbol/icons/Check'
import {
  defaultValues,
  validationSchema,
} from 'components/group-events/create-group-event-form-wizard'
import { CREATE_GROUP_EVENT } from 'components/group-events/create-group-event-form-wizard/queries'
import { hydrateEventFormValuesIntoForm } from 'components/group-events/create-group-event-form-wizard/hooks'
import SuccessStep from 'components/group-events/create-group-event-form-wizard/steps/success'
import {
  composeMaxAttendance,
  isOnlinePayment,
  MANUAL_PAYMENT,
  ALL_AGE_GROUP_TAGS,
  LEGACY_ADULTS_ONLY_TAG,
  LEGACY_JUNIORS_ONLY_TAG,
} from 'utils/groupEventUtils'
import {
  getOfferingsPath,
  OFFERINGS_TAB,
} from 'pages/pga-coach/bookings/offerings/routes'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'
import { getRegistrationOpenDateTime } from 'pages/pga-coach/bookings/events/registration-open-datetime'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import FormSectionContainer from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-section-container'
import EditEventSectionNav, {
  EDIT_EVENT_SECTIONS,
  getActiveSectionIndex,
} from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-section-nav'
import EditEventPagination from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-pagination'
import EventNameAndTags from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/event-name-and-tags'
import EventDescription from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/event-description'
import ScheduleSessions from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/schedule-sessions'
import AssistantCoaches from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/assistant-coaches'
import FeaturedImage from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/featured-image'
import Unlisted from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-sections/unlisted'
import AgeGroupSelect from 'components/age-group-select'
import CreateEventLocation from './form-sections/create-event-location'
import CreateEventRegistration from './form-sections/create-event-registration'
import CreateEventPayment from './form-sections/create-event-payment'
import CreateEventQuickStartStep from './form-sections/create-event-quick-start'
import { shouldShowCreateEventQuickStart } from './quick-start-visibility'

const CreateEventForm = ({
  coachFacilities,
  paymentAccounts,
  coachAcademies = [],
  coachFacilityLocations,
  showOnboardingModal,
  backUrl,
  canBuildOwnEvent = true,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useAuth()
  const [createdGroupEvent, setCreatedGroupEvent] = useState(null)
  const [showQuickStart, setShowQuickStart] = useState(() =>
    shouldShowCreateEventQuickStart({
      search: window.location.search,
      skipQuickStart: history.location.state?.skipQuickStart,
    }),
  )
  const [activeSection, setActiveSection] = useState(EDIT_EVENT_SECTIONS[0].id)
  const [isRecurringPaymentScheduleEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_GROUP_EVENT_RECURRING_PAYMENTS_ENABLED,
  ])

  const activeStepIndex = getActiveSectionIndex(activeSection)
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === EDIT_EVENT_SECTIONS.length - 1
  const currentSection = EDIT_EVENT_SECTIONS[activeStepIndex]

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveSection(sectionId)
      scrollToFormTop()
    },
    [scrollToFormTop],
  )

  const [createGroupEvent, { loading }] = useMutation(CREATE_GROUP_EVENT, {
    onError: error => {
      enqueueSnackbar(error.message, {
        variant: 'error',
      })
    },
    onCompleted: data => {
      if (data.createGroupEvent.success) {
        if (user.onboarding) {
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'group-event',
            formAction: 'completed-onboarding-create-group-event',
          })
        } else {
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'group-event',
            formAction: 'completed-create-group-event',
          })
        }
        setCreatedGroupEvent(data.createGroupEvent.groupEvent)
      } else {
        enqueueSnackbar(data.createGroupEvent.message, {
          variant: 'error',
        })
      }
    },
  })

  const form = useForm({
    defaultValues: {
      ...defaultValues,
    },
    shouldUnregister: false,
    resolver: yupResolver(
      validationSchema(
        coachFacilities,
        paymentAccounts,
        coachAcademies,
        coachFacilityLocations,
        isRecurringPaymentScheduleEnabled,
        user,
      ),
    ),
  })

  const { watch, trigger } = form

  useEffect(() => {
    if (!window.location.search.includes('preload')) return

    const timer = setTimeout(() => {
      hydrateEventFormValuesIntoForm(form)
      setShowQuickStart(false)
    }, 250)

    return () => clearTimeout(timer)
  }, [form])

  const handleEnterForm = useCallback(() => {
    setShowQuickStart(false)
    scrollToFormTop()
  }, [scrollToFormTop])

  const handleCancel = useCallback(() => {
    history.push(backUrl || getOfferingsPath(OFFERINGS_TAB.GROUP_EVENTS))
  }, [backUrl, history])

  const academyId = watch('academyId')
  const academyRequired = watch('academyRequired')
  const isExternalRegistration = watch('isExternalRegistration')
  const tags = watch('tags')
  const sessions = watch('sessions')
  const locationId = watch('locationId')
  const isPgaHopeEvent = tags?.includes('PGA HOPE')

  const selectedAcademy = useMemo(
    () => coachAcademies.find(academy => academy.id === academyId),
    [coachAcademies, academyId],
  )

  const selectedFacility = useMemo(
    () => coachFacilityLocations?.find(facility => facility.id === locationId),
    [coachFacilityLocations, locationId],
  )

  const eventStartDateTime = useMemo(() => {
    if (!sessions?.[0]?.startDate || !sessions?.[0]?.startTime) {
      return null
    }
    if (!selectedFacility?.timezone) {
      return null
    }

    try {
      return buildDateTime(
        sessions[0].startDate,
        sessions[0].startTime,
        selectedFacility.timezone,
      ).toISO()
    } catch (error) {
      return null
    }
  }, [sessions, selectedFacility])

  const scheduleEventStub = useMemo(
    () => (selectedAcademy ? { academy: selectedAcademy } : null),
    [selectedAcademy],
  )

  const getStepFields = useCallback(
    section => {
      const fields = [...(section.fields || [])]

      if (section.id === 'edit-section-basics') {
        return ['title', 'tags', 'ageGroup']
      }

      if (section.id === 'edit-section-location') {
        const locationFields = ['locationId']
        if (academyRequired) {
          locationFields.push('academyId')
        }
        return locationFields
      }

      if (section.id === 'edit-section-schedule') {
        return ['sessions', 'assistantCoachIds']
      }

      if (section.id === 'edit-section-registration') {
        const registrationFields = ['collectAttendance']

        if (isExternalRegistration) {
          registrationFields.push(
            'isExternalRegistration',
            'externalRegistrationUrl',
          )
        } else {
          registrationFields.push(
            'hoursBeforeRegistrationCloses',
            'registrationOpenDate',
            'registrationOpenTime',
            'maxAttendance',
            'accessCode',
            'waitlistEnabled',
          )
        }

        if (!isPgaHopeEvent) {
          registrationFields.push(
            'paymentRecurrenceSchedule',
            'price',
            'paymentAccountId',
          )
        }

        return registrationFields
      }

      if (section.id === 'edit-section-media') {
        const mediaFields = ['featuredImageUrl']
        if (!isExternalRegistration) {
          mediaFields.push('unlisted')
        }
        return mediaFields
      }

      return fields
    },
    [academyRequired, isExternalRegistration, isPgaHopeEvent],
  )

  const navigateToSectionWithError = useCallback(
    formErrors => {
      const errorFields = Object.keys(formErrors)
      const sectionWithError = EDIT_EVENT_SECTIONS.find(section => {
        const fields = getStepFields(section)
        return fields.some(field => errorFields.includes(field))
      })

      if (sectionWithError) {
        setActiveSection(sectionWithError.id)
        scrollToFormTop()
      }
    },
    [getStepFields, scrollToFormTop],
  )

  const showFirstValidationError = useCallback(
    formErrors => {
      const errorMessage = getFirstErrorMessage(formErrors)

      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveSection(EDIT_EVENT_SECTIONS[activeStepIndex - 1].id)
      scrollToFormTop()
    }
  }, [activeStepIndex, isFirstStep, scrollToFormTop])

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = getStepFields(currentSection)
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid) {
      setActiveSection(EDIT_EVENT_SECTIONS[activeStepIndex + 1].id)
      scrollToFormTop()
      return
    }

    const formErrors = form.formState.errors
    navigateToSectionWithError(formErrors)
    showFirstValidationError(formErrors)
  }, [
    activeStepIndex,
    currentSection,
    form.formState.errors,
    getStepFields,
    isLastStep,
    navigateToSectionWithError,
    scrollToFormTop,
    showFirstValidationError,
    trigger,
  ])

  const onSubmit = async formValues => {
    const ageGroupMap = new Map([
      ['adults-only', LEGACY_ADULTS_ONLY_TAG],
      ['juniors-only', LEGACY_JUNIORS_ONLY_TAG],
    ])
    const filteredTags = formValues.tags.filter(
      tag => !ALL_AGE_GROUP_TAGS.includes(tag),
    )
    const ageGroupValue = ageGroupMap.get(formValues.ageGroup)
    if (ageGroupValue) {
      filteredTags.push(ageGroupValue)
    }
    const isHopeEvent = filteredTags.includes('PGA HOPE')

    const paymentRecurrenceSchedule = isHopeEvent
      ? MANUAL_PAYMENT
      : formValues.paymentRecurrenceSchedule
    const price = isHopeEvent ? '0' : formValues.price

    const facility = coachFacilityLocations?.find(
      cf => cf.id === formValues.locationId,
    )
    const startDate = formValues.sessions[0].startDate
    const startTime = formValues.sessions[0].startTime
    const startDateTime = buildDateTime(startDate, startTime, facility.timezone)

    return createGroupEvent({
      variables: {
        groupEvent: {
          title: formValues.title,
          ...(formValues.academyId && { academyId: formValues.academyId }),
          academyRequired: formValues.academyRequired || !!formValues.academyId,
          assistantCoachIds: formValues.assistantCoachIds,
          description: formValues.description
            ? draftToMarkdown(JSON.parse(formValues.description))
            : null,
          registrationCloseDateTime: !formValues.isExternalRegistration
            ? startDateTime.minus({
              hours: formValues.hoursBeforeRegistrationCloses,
            })
            : null,
          registrationOpenDateTime: getRegistrationOpenDateTime(
            formValues,
            facility.timezone,
          ),
          tags: filteredTags,
          maxAttendance: composeMaxAttendance(
            formValues.maxAttendance,
            formValues.waitlistEnabled,
          ),
          unlisted: formValues.unlisted,
          priceInCents: dollarsToCents(price),
          externalRegistrationUrl: formValues.isExternalRegistration
            ? formValues.externalRegistrationUrl
            : null,
          featuredImageUrl: formValues.featuredImageUrl,
          paymentRecurrenceSchedule,
          paymentAccountId:
            isOnlinePayment({ paymentRecurrenceSchedule }) &&
            (formValues.paymentAccountId || '').length > 0
              ? formValues.paymentAccountId
              : null,
          accessCode:
            (formValues.accessCode || '').length > 0
              ? formValues.accessCode
              : null,
          sessions: formValues.sessions.map(session => ({
            facilityId: facility.id,
            startDateTime: buildDateTime(
              session.startDate,
              session.startTime,
              facility.timezone,
            ),
            endDateTime: buildDateTime(
              session.startDate,
              session.endTime,
              facility.timezone,
            ),
          })),
          waitlistEnabled: formValues.waitlistEnabled,
          collectAttendance: formValues.collectAttendance,
          templateId: formValues.templateId || null,
        },
      },
    })
  }

  const handleCreateClick = e => {
    if (!isLastStep) {
      e.preventDefault()
      return handleNext()
    }

    return form.handleSubmit(onSubmit, formErrors => {
      navigateToSectionWithError(formErrors)
      showFirstValidationError(formErrors)
    })(e)
  }

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'edit-section-basics':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <EventNameAndTags />
            <Box className={classes.fieldGroup}>
              <AgeGroupSelect className={classes.fieldInput} />
            </Box>
            <EventDescription />
          </FormSectionContainer>
        )
      case 'edit-section-location':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <CreateEventLocation
              coachFacilities={coachFacilities}
              coachAcademies={coachAcademies}
            />
          </FormSectionContainer>
        )
      case 'edit-section-schedule':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            {scheduleEventStub ? (
              <AssistantCoaches event={scheduleEventStub} />
            ) : null}
            <ScheduleSessions timezone={selectedFacility?.timezone} />
          </FormSectionContainer>
        )
      case 'edit-section-registration':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <CreateEventRegistration
              eventStartDateTime={eventStartDateTime}
              timezone={selectedFacility?.timezone}
            />
            <CreateEventPayment
              paymentAccounts={paymentAccounts}
              coachFacilities={coachFacilities}
              coachAcademies={coachAcademies}
            />
          </FormSectionContainer>
        )
      case 'edit-section-media':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <FeaturedImage />
            {!isExternalRegistration ? <Unlisted /> : null}
          </FormSectionContainer>
        )
      default:
        return null
    }
  }

  if (createdGroupEvent) {
    return (
      <SuccessStep
        slug={createdGroupEvent.slug}
        onCreateAnother={() => setCreatedGroupEvent(null)}
        showOnboardingModal={showOnboardingModal}
      />
    )
  }

  return (
    <Box>
      <FormProvider {...form}>
        {showQuickStart ? (
          <CreateEventQuickStartStep
            canBuildOwnEvent={canBuildOwnEvent}
            onContinue={handleEnterForm}
            onCancel={handleCancel}
          />
        ) : (
          <form onSubmit={handleCreateClick}>
            <Box className={classes.actionBar}>
              <Button
                type="button"
                className={classes.cancelAction}
                startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.saveAction}
                disabled={!canBuildOwnEvent || !isLastStep}
                disableElevation
                startIcon={<CheckIcon fontSize="small" />}
              >
                Create Event
              </Button>
            </Box>

            {loading ? (
              <LinearProgress
                color="primary"
                className={classes.loadingStyles}
              />
            ) : null}
            <Container maxWidth="lg">
              <Box pt={4} pb={3}>
                <Box className={classes.layoutGrid}>
                  <Box className={classes.navColumn}>
                    <EditEventSectionNav
                      activeSection={activeSection}
                      onSectionClick={handleSectionClick}
                    />
                  </Box>

                  <Box className={classes.formColumn}>
                    {renderActiveSection()}
                    <EditEventPagination
                      isFirstStep={isFirstStep}
                      isLastStep={isLastStep}
                      onPrevious={handlePrevious}
                      onNext={handleNext}
                    />
                  </Box>
                </Box>
              </Box>
            </Container>
          </form>
        )}
      </FormProvider>
    </Box>
  )
}

export default CreateEventForm
